import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { valueDiffers } from './sampleFilterStoreUtils';
import { createUserScopedJSONStorage, readUserScopedJson } from '@/lib/browser/userScopedStorage';
import { SampleSearchKind, type SampleSearchCriteria } from '@/api/ApiClient';
import { sanitizeSubstrateColumnVisibilityModel } from '@/lib/tables/sampleGridColumns';
import { reviveSampleSearchCriteriaDates } from '@/lib/utils/reviveSampleSearchCriteriaDates';
import type { FilterSourceFlags } from '@/stores/sampleFilterStore';

const STORAGE_KEY = 'substrateFilters';
export const SUBSTRATE_COLUMN_VISIBILITY_STORAGE_KEY = 'substrateColumnVisibility';
export const SUBSTRATE_SORT_MODEL_STORAGE_KEY = 'substrateGridSortModel';

/**
 * Default filters for the Substrates page: same sample search API, but always limited to
 * specimens with no synthesis (growth) activity yet. Growth-related criteria stay empty.
 */
export const substrateDefaultFilterCriteria: SampleSearchCriteria = {
  sampleLabel: '',
  projectId: '',
  status: '',
  addedByUserId: '',
  addedByResearchGroup: '',
  grownByUserId: '',
  grownByResearchGroup: '',
  reportingCategory: '',
  addedAfter: undefined,
  addedBefore: undefined,
  grownAfter: undefined,
  grownBefore: undefined,
  includeSplitPieces: false,
  includeFinalized: true,
  techniqueId: '',
  instrumentId: '',
  characterizationTechniqueId: '',
  characterizationInstrumentId: '',
  materials: [],
  shipped: '',
  shippedAfter: undefined,
  shippedBefore: undefined,
  substrateName: '',
  substrateVendor: '',
  substrateLot: '',
  substrateOrientation: '',
  substratePurity: undefined,
  multiSampleGrowthOnly: false,
  specimenType: 'substrates',
  kind: SampleSearchKind.Active,
};

/** Defaults for substrates grid (growth columns are not defined on that grid at all). */
export const substrateDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  projectLabel: true,
  sampleLabel: true,
  alias: false,
  creationDate: true,
  substrate: true,
  reportingCategory: false,
  lastActivity: true,
  owner: true,
  status: true,
  addedBy: false,
  lastActivityDate: false,
  lastChangedDate: false,
  lastChangedBy: false,
  transferred: false,
  shipped: false,
  faculty: false,
  characterizationTechniquesNames: false,
  characterizationInstrumentsNames: false,
  substrateName: true,
  substrateVendor: true,
  substrateLot: true,
  substrateOrientation: true,
  substratePurity: true,
};

export function loadSubstrateStoredColumnVisibility(): GridColumnVisibilityModel {
  if (typeof window === 'undefined') return substrateDefaultColumnVisibilityModel;

  try {
    const stored = readUserScopedJson(SUBSTRATE_COLUMN_VISIBILITY_STORAGE_KEY);
    if (!stored) return substrateDefaultColumnVisibilityModel;
    const parsed = JSON.parse(stored) as GridColumnVisibilityModel;
    return sanitizeSubstrateColumnVisibilityModel(parsed, substrateDefaultColumnVisibilityModel);
  } catch {
    return substrateDefaultColumnVisibilityModel;
  }
}

/** Keys ignored when showing the Filter badge dot on the Substrates page. */
const SUBSTRATE_EXTENDED_FILTER_SKIP_KEYS = new Set<keyof SampleSearchCriteria>([
  'specimenType',
  'kind',
  'grownAfter',
  'grownBefore',
  'grownByUserId',
  'grownByResearchGroup',
  'techniqueId',
  'instrumentId',
  'multiSampleGrowthOnly',
]);

export function hasSubstrateExtendedFiltersSet(
  filters: SampleSearchCriteria,
  sourceFlags?: FilterSourceFlags | null
): boolean {
  const def = substrateDefaultFilterCriteria;
  const keys = Object.keys(def) as (keyof SampleSearchCriteria)[];

  for (const key of keys) {
    if (SUBSTRATE_EXTENDED_FILTER_SKIP_KEYS.has(key)) continue;

    const a = filters[key];
    const b = def[key];
    if (!valueDiffers(a, b)) continue;

    if (key === 'sampleLabel' || key === 'projectId') continue;
    if (key === 'addedByUserId') {
      if (sourceFlags?.addedBySetFromBasicRow) continue;
      return true;
    }
    return true;
  }
  return false;
}

interface SubstrateFilterStore {
  filters: SampleSearchCriteria;
  sampleGridApplyGeneration: number;
  isSearching: boolean;
  addedBySetFromBasicRow: boolean;
  setIsSearching: (isSearching: boolean) => void;
  setFilters: (filters: SampleSearchCriteria, options?: FilterSourceFlags) => void;
  resetFilters: () => void;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
  ensureFreshSampleGridServerRequest: () => void;
}

export const useSubstrateFilterStore = create<SubstrateFilterStore>()(
  persist(
    (set, get) => ({
      filters: substrateDefaultFilterCriteria,
      sampleGridApplyGeneration: 0,
      isSearching: false,
      addedBySetFromBasicRow: false,
      setIsSearching: (isSearching) => set({ isSearching }),
      setFilters: (filters, options) =>
        set({
          filters: reviveSampleSearchCriteriaDates({
            ...filters,
            specimenType: 'substrates',
          }),
          addedBySetFromBasicRow: options?.addedBySetFromBasicRow ?? false,
        }),
      resetFilters: () =>
        set({
          filters: substrateDefaultFilterCriteria,
          addedBySetFromBasicRow: false,
        }),
      paginationModel: { page: 0, pageSize: 25 },
      setPaginationModel: (modelOrUpdater) => {
        const current = get().paginationModel;
        const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
        set({ paginationModel: newModel });
      },
      ensureFreshSampleGridServerRequest: () =>
        set((state) => ({ sampleGridApplyGeneration: state.sampleGridApplyGeneration + 1 })),
    }),
    {
      name: STORAGE_KEY,
      storage: createUserScopedJSONStorage(),
      partialize: (state) => ({
        filters: state.filters,
        paginationModel: state.paginationModel,
        addedBySetFromBasicRow: state.addedBySetFromBasicRow,
      }),
      merge: (persistedState, currentState) => {
        const p = persistedState as
          | Partial<Pick<SubstrateFilterStore, 'filters' | 'paginationModel' | 'addedBySetFromBasicRow'>>
          | undefined
          | null;
        const c = currentState as SubstrateFilterStore;
        if (!p) return c;
        return {
          ...c,
          ...p,
          filters: reviveSampleSearchCriteriaDates({
            ...substrateDefaultFilterCriteria,
            ...(p.filters ?? {}),
            specimenType: 'substrates',
          }),
          paginationModel: p.paginationModel ?? c.paginationModel,
          addedBySetFromBasicRow: p.addedBySetFromBasicRow ?? c.addedBySetFromBasicRow,
        };
      },
    }
  )
);
